import Layout from "@/components/feature/Layout";
import PageHero from "@/components/base/PageHero";
import SectionHeader from "@/components/base/SectionHeader";
import CTAPanel from "@/components/base/CTAPanel";
import RelatedContent from "@/components/feature/RelatedContent";

const serviceAreas = [
  {
    title: "Database Engineering",
    description: "Mitaja has supported database engineering activities for enterprise environments, including design, implementation, and optimization of relational database systems.",
    icon: "ri-folder-3-line",
  },
  {
    title: "Database Administration",
    description: "Database administration support for Oracle and SQL Server environments, including routine maintenance, monitoring, and performance tuning.",
    icon: "ri-folder-3-line",
  },
  {
    title: "Oracle",
    description: "Oracle database support and administration within enterprise federal environments.",
    icon: "ri-folder-3-line",
  },
  {
    title: "SQL / SQL Server",
    description: "SQL Server and SQL-based database administration and engineering support for enterprise platforms and applications.",
    icon: "ri-folder-3-line",
  },
  {
    title: "Schema Management",
    description: "Database schema management and structural support for relational database environments.",
    icon: "ri-folder-3-line",
  },
  {
    title: "Database Platform Support",
    description: "Enterprise database platform support encompassing multiple relational database technologies within mission-critical environments.",
    icon: "ri-folder-3-line",
  },
  {
    title: "Data Integrity",
    description: "Support for data integrity and database consistency within enterprise data platforms.",
    icon: "ri-folder-3-line",
  },
];

export default function DatabaseDataPlatform() {
  return (
    <Layout>
      <PageHero
        title="Database & Data Platform Services"
        subtitle="Proven Core Capability"
        description="Database engineering, administration, and platform support for Oracle, SQL Server, and relational database environments."
        size="md"
      />

      <section className="section-padding bg-white">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <SectionHeader
                title="Service Areas"
                centered={false}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {serviceAreas.map((area) => (
                  <div key={area.title} className="card-base p-6">
                    <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                      <i className={`${area.icon} text-primary-600`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-foreground-900 mb-2">
                      {area.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {area.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="bg-background-100 rounded-lg p-6 border border-background-200/80">
                <h3 className="text-sm font-bold text-primary-800 uppercase tracking-wider mb-3">
                  Technologies
                </h3>
                <div className="flex flex-wrap gap-2">
                  {["Oracle", "SQL Server", "SQL", "Relational Databases", "Schema Management", "Data Integrity"].map((tag) => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-white text-primary-700 font-medium border border-primary-100">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <RelatedContent
        subtitle="Related Information"
        title="Database Experience & Delivery Details"
        description="See the documented federal database experience behind this capability and how to engage Mitaja."
        links={[
          {
            label: "U.S. Government Database Experience",
            description: "Documented Oracle and SQL Server engineering, administration, and platform support within federal IT environments.",
            href: "/experience/usa-government",
            icon: "ri-database-2-line",
          },
          {
            label: "Capability Statement",
            description: "Review Mitaja's capability statement for procurement, teaming, and qualification review.",
            href: "/resources/capability-statement",
            icon: "ri-file-list-line",
          },
          {
            label: "Contact Mitaja",
            description: "Discuss database engineering, administration, or teaming requirements with our team.",
            href: "/contact",
            icon: "ri-mail-line",
          },
        ]}
      />

      <CTAPanel
        title="Need Database Expertise?"
        description="Mitaja provides qualified database engineers, administrators, and technical resources for enterprise and government database programs."
        secondaryAction={{ label: "View Past Performance", href: "/experience" }}
        variant="dark"
      />
    </Layout>
  );
}